<template>
    <DCard padding>
        <DFormRow
            :label="__('customers.delivery_notes.form.order_number')"
        >
            <DInput
                id="delivery-note-order-number"
                v-model="form.order_number"
                :invalid-feedback="errors.order_number"
            />
        </DFormRow>
        <DFormRow
            :label="__('customers.delivery_notes.form.customer_cost_center')"
        >
            <DInput
                id="delivery-note-customer-cost-center"
                v-model="form.customer_cost_center"
                :invalid-feedback="errors.customer_cost_center"
            />
        </DFormRow>
        <DFormRow
            :label="__('customers.delivery_notes.form.alternative_address')"
        >
            <DFormTextarea
                id="delivery-note-alternative-address"
                v-model="form.alternative_address"
                :invalid-feedback="errors.alternative_address"
            />
        </DFormRow>
        <DRow class="justify-content-center">
            <h5>
                Contracts
                <span
                    v-if="errors.contracts"
                    class="text-danger"
                >
                    ({{ errors.contracts }})
                </span>
            </h5>
        </DRow>
        <DRow>
            <DTable
                :items="contracts"
                :fields="fields"
            >
                <template
                    #checkbox-header
                >
                    <DFormCheck
                        class="absolute-checkbox"
                        :checked="form.contracts.length === contracts.length"
                        @click="selectAllContracts"
                    />
                </template>
                <template #checkbox="{item}">
                    <td>
                        <DFormCheck
                            :id="`delivery_note_contract_${item.id}`"
                            :checked="form.contracts.includes(item.id)"
                            @click="saveItem(item.id)"
                        />
                    </td>
                </template>
                <template #description="{item}">
                    <td>
                        {{ item.product.name }}
                    </td>
                </template>
                <template #quantity="{item}">
                    <td>
                        {{ getQuantity(item.fulfillment_contract_config_values) }}
                    </td>
                </template>
                <template #invoice_notes="{item}">
                    <td>
                        {{ item.notes_invoice ?? '' }}
                    </td>
                </template>
            </DTable>
        </DRow>
    </DCard>
    <DFormControls
        :cancel-route="cancelRoute"
        :submit-message="__('customers.delivery_notes.buttons.preview')"
        submit-icon="eye"
        @submit="previewDocument"
    />
    <DModal
        :show="showPreviewModal"
        title="Preview"
        is-big
        is-confirm
        @confirm="submitForm"
        @close="closeModal"
    >
        <DRow>
            <DCol md="12">
                <DAlert
                    v-if="error"
                    class="m-0"
                    color="info"
                >
                    {{ error }}
                </DAlert>
                <DCard
                    v-else
                    class="pt-0"
                >
                    <DCardBody>
                        <pdf
                            :key="pdfDocument.fileName"
                            :src="pdfDocument.source"
                            :page="1"
                        />
                    </DCardBody>
                </DCard>
            </DCol>
        </DRow>
    </DModal>
</template>

<script>
import CustomerBase from '~@/Pages/Customers/Base'
import App from '~@/App.vue'
import { useForm } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { __ } from '~@/Composables/translation'
import { computed, ref } from 'vue'

import pdf from 'vue3-pdf'

export default {
    layout: [App, CustomerBase]
}

</script>

<script setup>

const fields = [
    { key: 'checkbox', label: '' },
    { key: 'id', label: __('customers.delivery_notes.contracts_table.id') },
    { key: 'description', label: __('customers.delivery_notes.contracts_table.description') },
    { key: 'invoice_notes', label: __('customers.delivery_notes.contracts_table.invoice_notes') },
    { key: 'quantity', label: __('customers.delivery_notes.contracts_table.quantity') }
]

const props = defineProps({
    contracts: {
        type: Array,
        default: () => { return [] }
    },
    errors: {
        type: Object,
        default: () => {
            return {}
        }
    },
    customer: {
        type: Object,
        default: () => {
            return {}
        }
    }
})

const form = useForm({
    order_number: null,
    customer_cost_center: null,
    alternative_address: null,
    contracts: []
})

const cancelRoute = computed(function () {
    return route('customers.customers.delivery_notes.index', { customer: props.customer.id })
})

const submitForm = () => form.post(route('customers.customers.delivery_notes.store', { customer: props.customer.id }))

function saveItem (item) {
    if (form.contracts.includes(item)) {
        form.contracts.splice(form.contracts.indexOf(item), 1)
    } else {
        form.contracts.push(item)
    }
}

function getQuantity (configValues) {
    const quantity = configValues.find(value => value.key === 'quantity')
    if (typeof quantity === 'undefined') {
        return 1
    }

    return quantity.value
}

const showPreviewModal = ref(false)
const pdfDocument = ref({})
const errorsData = ref(props.errors)
const error = ref(null)

function previewDocument () {
    window.axios.get(route('customers.customers.delivery_notes.preview', {
        customer: props.customer.id,
        _query: {
            order_number: form.order_number,
            customer_cost_center: form.customer_cost_center,
            alternative_address: form.alternative_address,
            contracts: form.contracts
        }
    }))
        .then(response => {
            const responseData = response.data.documentPdf
            const fileBlob = createBlob(responseData.content, responseData.mimetype)
            pdfDocument.value.source = URL.createObjectURL(fileBlob)
            pdfDocument.value.fileName = responseData.filename

            showPreviewModal.value = true
        })
        .catch((error) => {
            if (error && error.response && error.response.data.errors) {
                errorsData.value.contracts = error.response.data.errors.contracts[0]
            }
            if (error && error.response && error.response.data.error) {
                error.value = error.response.data.error
                showPreviewModal.value = true
            }
        })
}

function closeModal () {
    showPreviewModal.value = false
}

function createBlob (base64String, contentType) {
    const blobPdfFromBase64String = (base64String, contentType) => {
        const byteArray = Uint8Array.from(
            atob(base64String)
                .split('')
                .map(char => char.charCodeAt(0))
        )
        return new Blob([byteArray], { type: contentType })
    }

    return blobPdfFromBase64String(base64String, contentType)
}

function selectAllContracts () {
    const contractIds = props.contracts.map(contract => contract.id)
    if (form.contracts.length !== contractIds.length) {
        form.contracts = contractIds
    } else {
        form.contracts = []
    }
}
</script>
